import { CookieSettingsButton } from '@/components/CookieSettingsButton';
import { ReaderAppCallout } from '@/components/marketplace/ReaderAppCallout';
import { FacebookLogo } from '@/components/shared/FacebookLogo';
import { Link } from 'react-router-dom';
import { company } from '@/config/company';

const FACEBOOK_GROUP_URL = 'https://www.facebook.com/groups/selfpublishingforentrepreneurs';

const SOCIAL_LINKS = [
  {
    name: 'TikTok',
    href: 'https://www.tiktok.com/@bookstera_com',
    icon: (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.24V2h-3.12v13.17a2.74 2.74 0 1 1-2.74-2.74c.31 0 .61.05.9.15V9.4a5.86 5.86 0 0 0-.9-.07A5.86 5.86 0 1 0 15.82 15V8.32a7.9 7.9 0 0 0 4.63 1.49V6.69h-.86Z" />
      </svg>
    ),
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/bookstera',
    icon: <FacebookLogo />,
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/bookstera_com',
    icon: (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.8A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95h-8.5Zm8.95 1.35a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.8A3.2 3.2 0 1 0 12 15.2 3.2 3.2 0 0 0 12 8.8Z" />
      </svg>
    ),
  },
  {
    name: 'YouTube',
    href: 'https://www.youtube.com/@bookstera_com',
    icon: (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M23.5 6.2a3 3 0 0 0-2.1-2.12C19.54 3.5 12 3.5 12 3.5s-7.54 0-9.4.58A3 3 0 0 0 .5 6.2 31.3 31.3 0 0 0 0 12a31.3 31.3 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.12c1.86.58 9.4.58 9.4.58s7.54 0 9.4-.58a3 3 0 0 0 2.1-2.12A31.3 31.3 0 0 0 24 12a31.3 31.3 0 0 0-.5-5.8ZM9.6 15.7V8.3l6.4 3.7-6.4 3.7Z" />
      </svg>
    ),
  },
] as const;

type FooterLink = { label: string; href: string; badge?: string };

const footerColumns: { title: string; links: FooterLink[]; secondary?: { title: string; links: FooterLink[] } }[] = [
  {
    title: 'Product',
    links: [
      { label: 'Create Books', href: '/create' },
      { label: 'Growth Autopilot', href: '/growth-autopilot', badge: 'NEW' },
      { label: 'Marketplace', href: '/marketplace' },
      { label: 'Authors', href: '/authors' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Ebook Funnel Builder', href: '/funnel' },
      { label: 'Enterprise Solutions', href: '/enterprise/solutions' },
    ],
    secondary: {
      title: 'Features',
      links: [
        { label: 'BookBuild™', href: '/features#bookbuild' },
        { label: 'AI ExpertiseExtractor™', href: '/features#ai-expertise-extractor' },
        { label: 'Premium Design Pack', href: '/features#premium-design-pack' },
        { label: 'AI Writing Assistant Pro™', href: '/features#ai-writing-assistant-pro' },
        { label: 'AI Funnel Suite™', href: '/features#ai-funnel-suite' },
      ],
    },
  },
  {
    title: 'Book Marketing',
    links: [
      { label: 'Book Marketing Guide', href: '/book-marketing' },
      { label: 'AI Book Marketing', href: '/book-marketing/ai-book-marketing' },
      { label: 'Social Media for Authors', href: '/book-marketing/social-media-for-authors' },
      { label: 'Self-Published Book Marketing', href: '/book-marketing/how-to-market-a-self-published-book' },
      { label: 'BookTok Marketing', href: '/book-marketing/booktok-marketing' },
      { label: 'Book Marketing Plan', href: '/book-marketing/book-marketing-plan' },
      { label: 'Book Launch Plan', href: '/book-marketing/book-launch-marketing-plan' },
      { label: 'Amazon KDP Marketing', href: '/book-marketing/amazon-kdp-marketing' },
      { label: 'Meta Ads for Books', href: '/book-marketing/meta-ads-for-books' },
    ],
    secondary: {
      title: 'Compare',
      links: [{ label: 'Bookstera vs Native', href: '/bookstera-vs-native' }, { label: 'Native Alternatives', href: '/native-alternatives' }],
    },
  },
  {
    title: 'Resources',
    links: [
      { label: 'Bookstera Blog', href: '/blog' },
      { label: 'Book Growth Hub', href: '/book-growth' },
      { label: 'Self-Publisher Playbooks', href: '/self-publisher-playbooks' },
      { label: 'AI Tools Directory', href: '/tools' },
      { label: 'Tool Categories', href: '/tools/categories' },
      { label: 'Directory Methodology', href: '/tools/methodology' },
      { label: 'Publishing on the Marketplace', href: '/features#bookstera-marketplace' },
      { label: 'Publishing to Amazon KDP', href: '/features#amazon-kdp' },
      { label: 'Exporting Your Ebooks', href: '/features#export-ebooks' },
    ],
    secondary: {
      title: 'Build a Book Business',
      links: [
        { label: 'Course Creation', href: '/business-models/course-creation' },
        { label: 'Consulting', href: '/business-models/consulting' },
        { label: 'Speaking', href: '/business-models/speaking' },
        { label: '4 Ways to Make Money with Ebooks', href: '/blog/4-ways-to-make-money-with-ebooks' },
        { label: 'From Idea to Full Ebook Funnel', href: '/blog/from-idea-to-full-ebook-funnel' },
        { label: 'ROI Ebook Marketing Strategy', href: '/blog/roi-ebook-marketing-strategy' },
      ],
    },
  },
  {
    title: 'Company',
    links: [
      { label: 'About / Founder', href: company.urls.about },
      { label: 'Success Stories', href: company.urls.successStories },
      { label: 'Customer Reviews', href: company.urls.reviews },
      { label: 'Is Bookstera Legit?', href: '/is-bookstera-legit' },
      { label: 'Press', href: '/press' },
      { label: 'Affiliate Program', href: '/affiliate' },
      { label: 'Strategic Partners', href: '/partners' },
      { label: 'Partnerships', href: '/partnerships' },
      { label: 'Help & FAQs', href: '/help' },
      { label: 'Contact Us', href: '/support' },
    ],
  },
];

const legalLinks: FooterLink[] = [
  { label: 'Privacy Policy', href: company.urls.privacy },
  { label: 'Terms of Service', href: company.urls.terms },
  { label: 'Refund Policy', href: company.urls.refundPolicy },
  { label: 'Acceptable Use Policy', href: '/acceptable-use-policy' },
  { label: 'Code of Conduct', href: '/code-of-conduct' },
  { label: 'Imprint', href: company.urls.imprint },
];

function FooterLinks({ links }: { links: FooterLink[] }) {
  return <ul className="mt-4 space-y-3 text-sm leading-6">{links.map(link => (
    <li key={link.href}>
      <Link to={link.href} className="inline-flex items-start gap-1.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-secondary">
        <span>{link.label}</span>
        {link.badge && <span className="mt-1 shrink-0 rounded-full bg-secondary px-1.5 py-0.5 text-[9px] font-bold uppercase leading-3 tracking-wide text-white">{link.badge}</span>}
      </Link>
    </li>
  ))}</ul>;
}

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t bg-white">

      <div className="container py-8 md:py-12">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,4fr)] lg:gap-8">
          <div className="max-w-sm">
            <Link to="/" className="mr-6 flex items-center space-x-2">
              <div className="flex items-center">
                <img
                  src="/lovable-uploads/359fa788-be22-44ec-9fdc-6201facc8206.png"
                  alt="Bookstera Logo"
                  className="h-8 w-8"
                />
                <span className="ml-2 text-xl font-bold">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-secondary to-primary-dark">
                    Bookstera
                  </span>
                </span>
              </div>
            </Link>
            <p className="mt-4 text-sm text-muted-foreground">
              Create, publish, grow and sell your books with Bookstera.
            </p>
            <div className="mt-4 flex items-center gap-3">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
          <nav aria-label="Footer navigation" className="grid min-w-0 grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-4 lg:gap-x-8">
            {footerColumns.map(column => (
              <section key={column.title} className="min-w-0">
                <h2 className="text-sm font-semibold">{column.title}</h2>
                <FooterLinks links={column.links} />
                {column.secondary && <div className="mt-8">
                  <h3 className="text-sm font-semibold">{column.secondary.title}</h3>
                  <FooterLinks links={column.secondary.links} />
                </div>}
              </section>
            ))}
          </nav>
        </div>

        <ReaderAppCallout className="mt-10" />

        <a
          href={FACEBOOK_GROUP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-blue-950 transition hover:border-blue-300 hover:bg-blue-100"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1877f2] text-white">
            <FacebookLogo className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1 text-sm">
            <span className="font-bold">Join the Bookstera Facebook Group</span>
            <span className="hidden text-blue-800 sm:inline"> · Publishing tips, prompts, and community feedback</span>
          </span>
          <span className="shrink-0 text-xs font-bold text-[#1877f2]">
            Join now <span aria-hidden="true">&rarr;</span>
          </span>
        </a>

        <div className="mt-8 border-t pt-6 text-center text-muted-foreground">
          <nav aria-label="Legal and privacy" className="mb-6">
            <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-xs leading-5">
              {legalLinks.map(link => <li key={link.href}><Link to={link.href} className="transition-colors hover:text-foreground">{link.label}</Link></li>)}
              <li><CookieSettingsButton className="h-auto p-0 text-xs font-normal text-muted-foreground hover:text-foreground" /></li>
            </ul>
          </nav>
          <p className="text-center text-sm text-muted-foreground">
            © {currentYear} Bookstera®. All rights reserved.
          </p>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Operated by {company.legalName} · {company.companyNumber} · {company.city}, {company.country} · Founder:{' '}
            <Link className="underline hover:text-foreground" to={company.urls.about}>{company.founder}</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
